import {
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  message,
  Spin,
} from "antd";
import {
  useTranslation,
} from "react-i18next";
import {
  Search,
  Download,
} from "lucide-react";
import {
  type SalesCustomer,
} from "./SalesProductAnalyticsTabsDashboard.data";
import {
  getApiLangFromI18n,
} from "src/utils/apiLang";
import {
  getOptionalTenantId,
  fetchShopifySalesProductCustomersForExport,
  useShopifySalesProductCustomers,
} from "../hooks/useShopifySalesProductAnalytics";
import {
  getDashboardKpiLucideIconComponent,
} from "src/utils/dashboardKpiLucideIcon";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import {
  DashboardNarrativePanel,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DashboardSectionHeader,
  TablePagePagination,
  useDashboardTableSort,
  dashboardDataTable as dt,
} from "src/components/common";
import {
  useDashboardNarrative,
} from "src/hooks/useDashboardNarrative";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import {
  CustomerSegmentationKpi,
} from "./CustomerSegmentationKpi/CustomerSegmentationKpi";
import {
  PAGE_SIZE,
  formatCurrency,
  formatOrderDate,
  type CustomerTableSortKey,
  isDefaultCustomerTableSort,
  getCustomerApiSort,
  sortCustomerRows,
  customerSegmentTone,
  type CustomerExportLabels,
  exportCustomersCsv,
  exportCustomersXlsx,
  formatKpiChangePercent,
  localizeChartDigits,
  useComparisonLabelLocalizer,
  LocalizedMetricTooltip,
  SectionTitle,
  MetricCard,
  InventoryEmptyState,
} from "./salesProductShared";
import {
  useLanguage,
} from "src/hooks/useLanguage";

const NOT_AVAILABLE_SENTINEL = "Not Available";

const CustomerSegmentsCell: FC<{ segments: string[] }> = ({ segments }) => {
  const { t } = useTranslation();
  if (!segments.length) {
    return <span className={dt.empty}>{t("salesProduct.shared.notAvailable")}</span>;
  }

  const visible = segments.slice(0, 2);
  const hidden = segments.slice(2);

  return (
    <div className="sales-product-analytics-dashboard__customer-segments">
      {visible.map(segment => (
        <span
          key={segment}
          className={`sales-product-analytics-dashboard__segment-chip sales-product-analytics-dashboard__segment-chip--${customerSegmentTone(segment)}`}
          title={segment}
        >
          {segment}
        </span>
      ))}
      {hidden.length ? (
        <span className="sales-product-analytics-dashboard__segment-more">
          +{hidden.length}
          <span className="sales-product-analytics-dashboard__segment-tooltip">
            {hidden.map(segment => (
              <span
                key={segment}
                className={`sales-product-analytics-dashboard__segment-chip sales-product-analytics-dashboard__segment-chip--${customerSegmentTone(segment)}`}
              >
                {segment}
              </span>
            ))}
          </span>
        </span>
      ) : null}
    </div>
  );
};

const CustomerExportMenu: FC<{
  customers: SalesCustomer[];
  loadCustomers?: (_onProgress?: (_loaded: number, _total: number) => void) => Promise<SalesCustomer[]>;
}> = ({ customers, loadCustomers }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const hasRows = customers.length > 0;

  useEffect(() => {
    if (!open) { return; }
    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  // Headers, placeholders, file and sheet names all follow the UI language so an
  // Arabic dashboard produces an Arabic workbook rather than English column titles.
  const exportLabels = useMemo<CustomerExportLabels>(
    () => ({
      headers: {
        name: t("salesProduct.customers.columnName"),
        email: t("salesProduct.customers.columnEmail"),
        phone: t("salesProduct.customers.columnPhone"),
        segment: t("salesProduct.customers.columnSegment"),
        totalOrders: t("salesProduct.customers.columnTotalOrders"),
        totalSpend: t("salesProduct.customers.columnTotalSpend"),
        recentOrderDate: t("salesProduct.customers.columnRecentOrderDate"),
        signUpDate: t("salesProduct.customers.columnSignUpDate"),
      },
      notAvailable: t("salesProduct.shared.notAvailable"),
      notOrderedYet: t("salesProduct.shared.notOrderedYet"),
      fileName: t("salesProduct.customers.tableTitle"),
      sheetName: t("salesProduct.customers.tableTitle"),
    }),
    [t]
  );

  const handleExport = async (format: "csv" | "xlsx") => {
    if (!hasRows) {
      return;
    }
    setExporting(true);
    try {
      const rows = loadCustomers ? await loadCustomers() : customers;
      if (format === "csv") {
        exportCustomersCsv(rows, exportLabels);
      } else {
        exportCustomersXlsx(rows, exportLabels);
      }
      setOpen(false);
    } catch {
      message.error(t("salesProduct.customers.exportError"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={menuRef} className="sales-product-analytics-dashboard__export-menu">
      <button
        type="button"
        className={`sales-product-analytics-dashboard__export-button ${
          exporting ? "sales-product-analytics-dashboard__export-button--loading" : ""
        }`.trim()}
        onClick={() => setOpen(current => !current)}
        disabled={!hasRows || exporting}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download size={16} />
        <span>{t("salesProduct.shared.export")}</span>
        {exporting ? (
          <CircularLoader
            size={18}
            thickness={3}
            className="sales-product-analytics-dashboard__export-loader"
          />
        ) : null}
      </button>
      {open ? (
        <div className="sales-product-analytics-dashboard__export-dropdown" role="menu">
          <button type="button" role="menuitem" disabled={exporting} onClick={() => handleExport("csv")}>
            CSV
          </button>
          <button type="button" role="menuitem" disabled={exporting} onClick={() => handleExport("xlsx")}>
            XLSX
          </button>
        </div>
      ) : null}
    </div>
  );
};

export const CustomersSection: FC<{
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ dateRange, onDateRangeChange }) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const localizeComparisonLabel = useComparisonLabelLocalizer();
  const notAvailableLabel = t("salesProduct.shared.notAvailable");
  const localizeCustomerText = (value: string | undefined) =>
    !value || value.trim() === NOT_AVAILABLE_SENTINEL ? notAvailableLabel : value;
  const lang = getApiLangFromI18n(i18n);
  const tenantId = getOptionalTenantId();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasCustomerSortApplied, setHasCustomerSortApplied] = useState(false);
  const customerTableSort = useDashboardTableSort<CustomerTableSortKey>({
    key: "signUpDate",
    direction: "desc",
  });
  const customerApiSort = useMemo(
    () => hasCustomerSortApplied ? getCustomerApiSort(customerTableSort.sort) : undefined,
    [customerTableSort.sort, hasCustomerSortApplied],
  );
  const { summary, customers, rawData, totalPages, loading, error, listError } = useShopifySalesProductCustomers(
    dateRange.startDate,
    dateRange.endDate,
    lang,
    tenantId,
    page,
    PAGE_SIZE,
    debouncedSearch,
    customerApiSort,
  );

  const s = summary;
  const sorted = useMemo(
    () => !hasCustomerSortApplied && isDefaultCustomerTableSort(customerTableSort.sort)
      ? customers
      : sortCustomerRows(customers, customerTableSort.sort),
    [customers, customerTableSort.sort, hasCustomerSortApplied],
  );
  const handleCustomerSortClick = (key: CustomerTableSortKey) => {
    setHasCustomerSortApplied(true);
    customerTableSort.toggleColumn(key);
  };
  const loadExportCustomers = async (onProgress?: (_loaded: number, _total: number) => void) =>
    {
      const rows = await fetchShopifySalesProductCustomersForExport(
          dateRange.startDate,
          dateRange.endDate,
          lang,
          tenantId,
          debouncedSearch,
          customerApiSort,
          onProgress,
        );
      return !hasCustomerSortApplied && isDefaultCustomerTableSort(customerTableSort.sort)
        ? rows
        : sortCustomerRows(rows, customerTableSort.sort);
    };
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [dateRange.startDate, dateRange.endDate, customerApiSort]);

  const segmentation = useMemo(() => (
    s?.segmentation?.length ? s.segmentation : [
      { label: "New", value: s?.newCustomers ?? 0 },
      { label: "Returning", value: s?.returningCustomers ?? 0 },
    ]
  ), [s]);
  const segmentNewCustomers = segmentation.find(item => item.label === "New")?.value;
  const segmentReturningCustomers = segmentation.find(item => item.label === "Returning")?.value;
  const newCustomers = segmentNewCustomers ?? s?.newCustomers ?? 0;
  const returningCustomers = segmentReturningCustomers ?? s?.returningCustomers ?? 0;
  const avgOrderValue =
    s && s.avgOrderValue > 0
      ? Math.round(s.avgOrderValue)
      : customers.length
        ? Math.round(customers.reduce((sum, customer) => sum + customer.avgOrderValue, 0) / customers.length)
        : 0;
  const customersInPeriodCount = s?.customersInPeriod ?? 0;
  const customerTabIcons = useMemo(
    () =>
      ["Customers in Period", "Customers Ordered", "Avg. Order Value"].map(
        getDashboardKpiLucideIconComponent,
      ),
    [],
  );
  const customersEmptyMessage = listError
    ? t("salesProduct.customers.detailsUnavailable")
    : t("salesProduct.customers.noMatches");
  const narrativePayloadData = useMemo(
    () => ({
      customerSummary: rawData.summary,
      customers: rawData.customers,
    }),
    [rawData.customers, rawData.summary],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "sales_product_analytics",
    tabKey: "customers",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    data: narrativePayloadData,
    enabled: !loading && !error && Boolean(rawData.summary || rawData.customers),
  });

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title={t("salesProduct.customers.header.title")}
        subtitle={t("salesProduct.customers.header.subtitle")}
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}

      <DashboardNarrativePanel
        narrative={narrative}
        loading={loading || narrativeLoading}
        aiSummary={aiSummary}
      />

      <Spin spinning={loading}>
        <div className="sales-product-analytics-dashboard__metrics sales-product-analytics-dashboard__metrics--three sales-product-analytics-dashboard__metrics--customers">
          <MetricCard
            label="Customers in Period"
            displayLabel={t("salesProduct.customers.kpi.customersInPeriod")}
            value={s?.customersInPeriodFormattedValue
              ?? localizeChartDigits(String(customersInPeriodCount), isRTL)}
            statusHint={t("salesProduct.customers.kpi.customersInPeriodHint")}
            changePercent={s?.customersInPeriodChangePercent ?? undefined}
            changePercentFormattedValue={formatKpiChangePercent(s?.customersInPeriodChangePercent, isRTL)}
            comparisonLabel={localizeComparisonLabel(s?.comparisonLabel)}
            tone={s?.customersInPeriodChangePercent !== undefined && s?.customersInPeriodChangePercent !== null
              ? (s.customersInPeriodChangePercent > 0 ? "positive" : s.customersInPeriodChangePercent < 0 ? "negative" : "neutral")
              : undefined}
            icon={customerTabIcons[0]}
            tooltipAlign="start"
          />
          <MetricCard
            label="Customers Ordered"
            displayLabel={t("salesProduct.customers.kpi.customersOrdered")}
            value={s?.totalCustomersOrderedFormattedValue
              ?? localizeChartDigits(String(s?.totalCustomersOrdered ?? 0), isRTL)}
            statusHint={t("salesProduct.customers.kpi.customersOrderedHint")}
            changePercent={s?.totalCustomersOrderedChangePercent ?? undefined}
            changePercentFormattedValue={formatKpiChangePercent(s?.totalCustomersOrderedChangePercent, isRTL)}
            comparisonLabel={localizeComparisonLabel(s?.comparisonLabel)}
            tone={s?.totalCustomersOrderedChangePercent !== undefined && s?.totalCustomersOrderedChangePercent !== null
              ? (s.totalCustomersOrderedChangePercent > 0 ? "positive" : s.totalCustomersOrderedChangePercent < 0 ? "negative" : "neutral")
              : "positive"}
            icon={customerTabIcons[1]}
            tooltipAlign="start"
          />
          <MetricCard
            label="Avg. Order Value"
            displayLabel={t("salesProduct.customers.kpi.avgOrderValue")}
            value={
              s && s.avgOrderValue > 0 && s.avgOrderValueFormattedValue
                ? s.avgOrderValueFormattedValue
                : localizeChartDigits(formatCurrency(avgOrderValue), isRTL)
            }
            statusHint={t("salesProduct.customers.kpi.avgOrderValueHint")}
            changePercent={s?.avgOrderValueChangePercent ?? undefined}
            changePercentFormattedValue={formatKpiChangePercent(s?.avgOrderValueChangePercent, isRTL)}
            comparisonLabel={localizeComparisonLabel(s?.comparisonLabel)}
            tone={s?.avgOrderValueChangePercent !== undefined && s?.avgOrderValueChangePercent !== null
              ? (s.avgOrderValueChangePercent > 0 ? "positive" : s.avgOrderValueChangePercent < 0 ? "negative" : "neutral")
              : undefined}
            icon={customerTabIcons[2]}
            tooltipAlign="end"
          />
        </div>

        <div className="sales-product-analytics-dashboard__grid">
          <section className="sales-product-analytics-dashboard__card sales-product-analytics-dashboard__card--full-span sales-product-analytics-dashboard__card--customer-segmentation">
            <SectionTitle
              title={t("salesProduct.customers.segmentationTitle")}
              tooltipKey="New vs Returning Customers"
              tooltipPreferSide={isRTL ? "left" : "right"}
            />
            <CustomerSegmentationKpi
              newCount={newCustomers}
              returningCount={returningCustomers}
              loading={loading}
            />
          </section>
        </div>

        <DashboardDataTablePanel
          as="section"
          className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--customers"
        >
          <DashboardDataTablePanelHeader>
            <div>
              <SectionTitle
                title={t("salesProduct.customers.tableTitle")}
                tooltipKey="All Customers"
                tooltipPreferSide={isRTL ? "left" : "right"}
              />
              <p className="sales-product-analytics-dashboard__card-subtitle">
                {t("salesProduct.customers.tableSubtitle")}
              </p>
            </div>
            <div className="sales-product-analytics-dashboard__panel-tools">
              <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
                <Search size={16} />
                <input
                  type="search"
                  placeholder={t("salesProduct.customers.searchPlaceholder")}
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                />
              </label>
              <CustomerExportMenu customers={sorted} loadCustomers={loadExportCustomers} />
            </div>
          </DashboardDataTablePanelHeader>
          <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
            <DashboardDataTableTable className="sales-product-analytics-dashboard__table sales-product-analytics-dashboard__table--customers">
              <thead>
                <tr>
                  <th>{t("salesProduct.customers.columnName")}</th>
                  <th>{t("salesProduct.customers.columnEmail")}</th>
                  <th>{t("salesProduct.customers.columnPhone")}</th>
                  <th className="sales-product-analytics-dashboard__tooltip-header">
                    <span className={dt.thHelpRow}>
                      {t("salesProduct.customers.columnSegment")}
                      <LocalizedMetricTooltip
                        label="Segment"
                        preferSide={isRTL ? "left" : "right"}
                      />
                    </span>
                  </th>
                  <th className={`${dt.number} sales-product-analytics-dashboard__tooltip-header`.trim()}>
                    <span className={dt.thHelpRow}>
                      <DashboardDataTableSortButton
                        onClick={() => handleCustomerSortClick("totalOrders")}
                      >
                        {t("salesProduct.customers.columnTotalOrders")}
                      </DashboardDataTableSortButton>
                      <LocalizedMetricTooltip
                        label="Total Orders"
                        align="end"
                        preferSide={isRTL ? "left" : "right"}
                      />
                    </span>
                  </th>
                  <th className={`${dt.number} sales-product-analytics-dashboard__tooltip-header`.trim()}>
                    <span className={dt.thHelpRow}>
                      <DashboardDataTableSortButton
                        onClick={() => handleCustomerSortClick("totalSpent")}
                      >
                        {t("salesProduct.customers.columnTotalSpend")}
                      </DashboardDataTableSortButton>
                      <LocalizedMetricTooltip
                        label="Total Spend"
                        align="end"
                        preferSide={isRTL ? "left" : "right"}
                      />
                    </span>
                  </th>
                  <th className="sales-product-analytics-dashboard__tooltip-header">
                    <span className={dt.thHelpRow}>
                      <DashboardDataTableSortButton
                        onClick={() => handleCustomerSortClick("recentOrderDate")}
                      >
                        {t("salesProduct.customers.columnRecentOrderDate")}
                      </DashboardDataTableSortButton>
                      <LocalizedMetricTooltip
                        label="Recent Order Date"
                        preferSide={isRTL ? "left" : "right"}
                      />
                    </span>
                  </th>
                  <th className="sales-product-analytics-dashboard__tooltip-header">
                    <span className={dt.thHelpRow}>
                      <DashboardDataTableSortButton
                        onClick={() => handleCustomerSortClick("signUpDate")}
                      >
                        {t("salesProduct.customers.columnSignUpDate")}
                      </DashboardDataTableSortButton>
                      <LocalizedMetricTooltip
                        label="Sign Up Date"
                        align="end"
                        preferSide={isRTL ? "left" : "right"}
                      />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length ? sorted.map(customer => (
                    <tr key={customer.id}>
                      <td className={dt.cellEmphasis}>{localizeCustomerText(customer.name)}</td>
                      <td>{localizeCustomerText(customer.email)}</td>
                      <td>{localizeCustomerText(customer.phone)}</td>
                      <td>
                        <CustomerSegmentsCell segments={customer.segments} />
                      </td>
                      <td className={dt.number}>
                        {customer.totalOrdersFormattedValue
                          ?? localizeChartDigits(String(customer.totalOrders), isRTL)}
                      </td>
                      <td className={`${dt.number} ${dt.cellEmphasis}`.trim()}>
                        {customer.totalSpentFormattedValue
                          ?? localizeChartDigits(formatCurrency(customer.totalSpent), isRTL)}
                      </td>
                      <td>
                        {customer.recentOrderDateFormattedValue
                          ?? (customer.recentOrderDate && customer.recentOrderDate.trim() !== NOT_AVAILABLE_SENTINEL
                            ? localizeChartDigits(formatOrderDate(customer.recentOrderDate), isRTL)
                            : t("salesProduct.shared.notOrderedYet"))}
                      </td>
                      <td>
                        {customer.signUpDateFormattedValue
                          ?? (customer.signUpDate && customer.signUpDate.trim() !== NOT_AVAILABLE_SENTINEL
                            ? localizeChartDigits(formatOrderDate(customer.signUpDate), isRTL)
                            : notAvailableLabel)}
                      </td>
                    </tr>
                  )) : !loading ? (
                    <tr>
                      <td className="sales-product-analytics-dashboard__inventory-empty-cell" colSpan={8}>
                        <InventoryEmptyState message={customersEmptyMessage} />
                      </td>
                    </tr>
                  ) : null}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
          <TablePagePagination
            currentPage={page}
            totalPages={totalPages}
            onPrevious={() => setPage(p => Math.max(1, p - 1))}
            onNext={() =>
              setPage(p => Math.min(totalPages, p + 1))
            }
          />
        </DashboardDataTablePanel>
      </Spin>
    </div>
  );
};
